import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "ginger_admin_password";

function LoginCard({ onAuth }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!pwd) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pwd });
      sessionStorage.setItem(STORAGE_KEY, pwd);
      onAuth(pwd);
      toast.success("Bienvenue.");
    } catch (e) {
      toast.error("Mot de passe invalide.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-shell">
      <div className="admin-login-card">
        <div className="form-title">Espace admin</div>
        <h1 className="admin-login-title">
          Ginger<span className="admin-dot">.</span>
        </h1>
        <p className="admin-login-sub">
          Tableau de bord des pré-inscriptions Tours.
        </p>
        <form onSubmit={submit} className="admin-login-form">
          <label className="field-label" htmlFor="pwd">
            Mot de passe <span className="req">*</span>
          </label>
          <input
            id="pwd"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="••••••••"
            autoFocus
            data-testid="admin-password-input"
          />
          <button type="submit" disabled={loading} data-testid="admin-login-btn">
            {loading ? "Vérification…" : "Entrer"}
            <span className="arrow">→</span>
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="admin-stat">
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">{value}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const SLOT_SHORT = {
  "lundi-18-mai-14h": "Lun 18/05 14h",
  "jeudi-21-mai-16h": "Jeu 21/05 16h",
};

const TYPE_SHORT = {
  eleve: { label: "Élève", className: "" },
  studio: { label: "Studio", className: "studio" },
  coach: { label: "Coach", className: "coach" },
};

function Dashboard({ password, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/preinscriptions`, {
        headers: { "X-Admin-Password": password },
      });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expirée.");
        onLogout();
      } else {
        toast.error("Échec du chargement.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/preinscriptions.csv`, {
        headers: { "X-Admin-Password": password },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ginger-preinscriptions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé.");
    } catch (e) {
      toast.error("Échec de l'export.");
    }
  };

  const deleteOne = async (it) => {
    const ok = window.confirm(
      `Supprimer la pré-inscription de ${it.prenom} ${it.nom} (${it.email}) ?\n\nCette action est définitive.`,
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/preinscriptions/${it.id}`, {
        headers: { "X-Admin-Password": password },
      });
      toast.success(`${it.prenom} ${it.nom} supprimé·e.`);
      load();
    } catch (e) {
      toast.error("Échec de la suppression.");
    }
  };

  const purgeFailed = async () => {
    const failedCount = (data?.items || []).filter(
      (it) => it.admin_email_status === "failed" && it.user_email_status === "failed",
    ).length;
    if (failedCount === 0) {
      toast.info("Aucune ligne de test à supprimer.");
      return;
    }
    const ok = window.confirm(
      `Supprimer toutes les ${failedCount} pré-inscriptions en ÉCHEC (test) ?\n\nLes vraies pré-inscriptions ne seront pas touchées.`,
    );
    if (!ok) return;
    try {
      const res = await axios.delete(`${API}/admin/preinscriptions?only_failed=true`, {
        headers: { "X-Admin-Password": password },
      });
      toast.success(`${res.data.deleted} ligne(s) de test supprimée(s).`);
      load();
    } catch (e) {
      toast.error("Échec de la purge.");
    }
  };

  const items = (data?.items || []).filter((it) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (it.prenom || "").toLowerCase().includes(q) ||
      (it.nom || "").toLowerCase().includes(q) ||
      (it.email || "").toLowerCase().includes(q) ||
      (it.tel || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="admin-shell">
      <header className="admin-top">
        <div>
          <div className="form-title" style={{ marginBottom: 0 }}>Ginger · admin</div>
          <h1 className="admin-h1">Inscriptions Ginger Circle</h1>
        </div>
        <div className="admin-top-actions">
          <button className="btn-ghost" onClick={load} data-testid="admin-refresh">↻ Rafraîchir</button>
          <button className="btn-ghost btn-ghost-danger" onClick={purgeFailed} data-testid="admin-purge-failed">
            🧹 Purger les tests
          </button>
          <button className="btn-cta honey" onClick={downloadCsv} data-testid="admin-export-csv">
            Exporter CSV <span className="arrow">→</span>
          </button>
          <button className="btn-ghost" onClick={onLogout} data-testid="admin-logout">Déconnexion</button>
        </div>
      </header>

      {loading && !data ? (
        <div className="admin-loading">Chargement…</div>
      ) : data ? (
        <>
          <section className="admin-stats">
            <StatCard
              label="Total"
              value={data.total}
              sub={data.total > 1 ? "inscrit·e·s" : "inscrit·e"}
            />
            {["eleve", "studio", "coach"].map((type) => {
              const count = (data.items || []).filter(
                (it) => (it.interest_type || "eleve") === type,
              ).length;
              const labelMap = { eleve: "Élèves", studio: "Studios", coach: "Coachs" };
              return (
                <StatCard
                  key={type}
                  label={labelMap[type]}
                  value={count}
                  sub={count > 1 ? "inscrits" : count === 1 ? "inscrit" : "—"}
                />
              );
            })}
          </section>

          <section className="admin-table-wrap">
            <div className="admin-table-head">
              <input
                className="admin-search"
                placeholder="Rechercher par nom, email…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="admin-search"
              />
              <span className="admin-count">
                {items.length} affiché{items.length > 1 ? "s" : ""}
              </span>
            </div>

            <div className="admin-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Ville</th>
                    <th>Note</th>
                    <th>Email</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="admin-empty">Aucune inscription pour le moment.</td>
                    </tr>
                  ) : (
                    items.map((it) => {
                      const t = TYPE_SHORT[it.interest_type] || TYPE_SHORT.eleve;
                      return (
                        <tr key={it.id} data-testid={`admin-row-${it.id}`}>
                          <td className="mono-cell">{fmtDate(it.created_at)}</td>
                          <td>
                            <span className={`type-tag ${t.className}`}>{t.label}</span>
                            {it.slots && it.slots.length > 0 && (
                              <div className="slot-tags" style={{ marginTop: 4 }}>
                                {it.slots.map((s) => (
                                  <span key={s} className="slot-tag">{SLOT_SHORT[s] || s}</span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>
                            <strong>{it.prenom} {it.nom}</strong>
                          </td>
                          <td>
                            <a href={`mailto:${it.email}`}>{it.email}</a>
                          </td>
                          <td>{it.tel || <span className="muted">—</span>}</td>
                          <td>{it.ville || <span className="muted">—</span>}</td>
                          <td className="note-cell">{it.note ? it.note : <span className="muted">—</span>}</td>
                          <td>
                            <span className={`status-pill status-${it.user_email_status}`}>
                              {it.user_email_status === "sent" ? "✓" : it.user_email_status === "failed" ? "✗" : "•"}
                            </span>
                          </td>
                          <td className="action-cell">
                            <button
                              className="btn-row-delete"
                              onClick={() => deleteOne(it)}
                              title="Supprimer cette inscription"
                              aria-label={`Supprimer ${it.prenom} ${it.nom}`}
                              data-testid={`admin-delete-${it.id}`}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

export default function Admin() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");

  // Validate cached password on mount
  useEffect(() => {
    if (!password) return;
    axios
      .post(`${API}/admin/login`, { password })
      .catch(() => {
        sessionStorage.removeItem(STORAGE_KEY);
        setPassword("");
      });
  }, [password]);

  const onLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
  };

  return (
    <>
      <Toaster position="top-center" richColors />
      {!password ? (
        <LoginCard onAuth={setPassword} />
      ) : (
        <Dashboard password={password} onLogout={onLogout} />
      )}
    </>
  );
}
